import React, { createContext, useContext, useEffect, useReducer, ReactNode } from 'react';
import { CompanionState, RegisteredObject, AddObjectFlow, PlatformCapabilities, ObjectConfiguration } from '../types/companion';
import { storage } from '../utils/localStorage';
import { objectManager, ObjectRegistrationData, getPlatformCapabilities } from '../utils/objects';

// Action types
type CompanionAction =
  | { type: 'LOAD_STATE'; payload: CompanionState }
  | { type: 'ADD_OBJECT'; payload: RegisteredObject }
  | { type: 'UPDATE_OBJECT'; payload: { id: string; updates: Partial<RegisteredObject> } }
  | { type: 'REMOVE_OBJECT'; payload: string }
  | { type: 'SET_FIRST_VISIT'; payload: boolean }
  | { type: 'SET_INSTALL_PROMPT'; payload: boolean }
  | { type: 'SET_ONBOARDING_COMPLETED'; payload: boolean }
  | { type: 'SET_ADD_FLOW'; payload: AddObjectFlow | null }
  | { type: 'REFRESH_OBJECTS' };

// Helper to map object ID to configuration key in ObjectConfiguration
function getConfigKeyForObject(objectId: string): keyof ObjectConfiguration | null {
  switch (objectId) {
    case 'fortune-webstones':
      return 'fortune';
    case 'phone-pillow':
      return 'phonePillow';
    // Add more object types here as needed
    default:
      return null;
  }
}

// Extended context interface
interface CompanionContextType extends CompanionState {
  // UI State
  addObjectFlow: AddObjectFlow | null;
  platformCapabilities: PlatformCapabilities;
  
  // Actions
  registerObject: (data: ObjectRegistrationData) => Promise<RegisteredObject | null>;
  updateObject: (id: string, updates: Partial<RegisteredObject>) => Promise<boolean>;
  updateObjectConfiguration: (id: string, configuration: Partial<ObjectConfiguration>) => Promise<boolean>;
  removeObject: (id: string) => Promise<boolean>;
  markFirstVisitComplete: () => void;
  markInstallPromptShown: () => void;
  completeOnboarding: () => void;
  setAddObjectFlow: (flow: AddObjectFlow | null) => void;
  refreshObjects: () => void;
  
  // Utility actions
  clearAllData: () => void;
  exportData: () => CompanionState | null;
  importData: (data: Partial<CompanionState>) => boolean;
}

// Initial state
const initialState: CompanionState = {
  registeredObjects: [],
  isFirstVisit: true,
  showInstallPrompt: true,
  hasCompletedOnboarding: false,
};

// Reducer
function companionReducer(state: CompanionState, action: CompanionAction): CompanionState {
  switch (action.type) {
    case 'LOAD_STATE':
      return action.payload;
    
    case 'ADD_OBJECT':
      return {
        ...state,
        registeredObjects: [...state.registeredObjects, action.payload],
      };
    
    case 'UPDATE_OBJECT':
      return {
        ...state,
        registeredObjects: state.registeredObjects.map(obj =>
          obj.id === action.payload.id 
            ? { ...obj, ...action.payload.updates }
            : obj
        ),
      };
    
    case 'REMOVE_OBJECT':
      return {
        ...state,
        registeredObjects: state.registeredObjects.filter(obj => obj.id !== action.payload),
      };
    
    case 'SET_FIRST_VISIT':
      return {
        ...state,
        isFirstVisit: action.payload,
      };
    
    case 'SET_INSTALL_PROMPT':
      return {
        ...state,
        showInstallPrompt: action.payload,
      };

    case 'SET_ONBOARDING_COMPLETED':
      return {
        ...state,
        hasCompletedOnboarding: action.payload,
      };
    
    case 'REFRESH_OBJECTS':
      const refreshedObjects = storage.getRegisteredObjects();
      return {
        ...state,
        registeredObjects: refreshedObjects,
      };
    
    default:
      return state;
  }
}

// UI reducer for non-persistent state
interface UIState {
  addObjectFlow: AddObjectFlow | null;
}

type UIAction = 
  | { type: 'SET_ADD_FLOW'; payload: AddObjectFlow | null };

function uiReducer(state: UIState, action: UIAction): UIState {
  switch (action.type) {
    case 'SET_ADD_FLOW':
      return { ...state, addObjectFlow: action.payload };
    default:
      return state;
  }
}

const initialUIState: UIState = {
  addObjectFlow: null,
};

// Create contexts
const CompanionContext = createContext<CompanionContextType | null>(null);

// Provider component
export function CompanionProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(companionReducer, initialState);
  const [uiState, uiDispatch] = useReducer(uiReducer, initialUIState);
  const [platformCapabilities] = React.useState<PlatformCapabilities>(getPlatformCapabilities());

  // Load initial state from storage
  useEffect(() => {
    const loadedState = storage.getCompanionState();
    dispatch({ type: 'LOAD_STATE', payload: loadedState });
  }, []);

  // Actions
  const registerObject = async (data: ObjectRegistrationData): Promise<RegisteredObject | null> => {
    try {
      // If tagId is provided, register with server first
      if (data.tagId) {
        const { registerTag } = await import('../utils/api');
        const ObjectType = await import('../utils/api').then(m => m.ObjectType);

        // Determine object type for server registration
        const availableObject = objectManager.getAvailableObject(data.objectId);
        if (!availableObject) {
          console.error('Unknown object type:', data.objectId);
          return null;
        }

        // Map objectId to server ObjectType
        let serverObjectType: typeof ObjectType.Fortune | typeof ObjectType.WornMemory;
        if (data.objectId === 'fortune-webstones') {
          serverObjectType = ObjectType.Fortune;
        } else if (data.objectId.includes('worn-memory')) {
          serverObjectType = ObjectType.WornMemory;
        } else {
          console.error('Unsupported object type for server registration:', data.objectId);
          return null;
        }

        // Register tag with server
        await registerTag({
          tagId: data.tagId,
          objectType: serverObjectType,
        });
      }

      // Create local registration
      const newObject = objectManager.registerObject(data);
      if (newObject) {
        dispatch({ type: 'ADD_OBJECT', payload: newObject });
        return newObject;
      }
      return null;
    } catch (error) {
      console.error('Failed to register object:', error);
      return null;
    }
  };

  const updateObject = async (
    id: string,
    updates: Partial<RegisteredObject>
  ): Promise<boolean> => {
    try {
      const success = storage.updateRegisteredObject(id, updates);
      if (success) {
        dispatch({ type: 'UPDATE_OBJECT', payload: { id, updates } });
      }
      return success;
    } catch (error) {
      console.error('Failed to update object:', error);
      return false;
    }
  };

  const updateObjectConfiguration = async (
    id: string,
    configuration: Partial<ObjectConfiguration>
  ): Promise<boolean> => {
    const existing = state.registeredObjects.find(obj => obj.id === id);
    if (!existing) return false;

    return updateObject(id, {
      configuration: {
        ...existing.configuration,
        ...configuration,
      },
    });
  };

  const removeObject = async (id: string): Promise<boolean> => {
    try {
      const success = storage.removeRegisteredObject(id);
      if (success) {
        dispatch({ type: 'REMOVE_OBJECT', payload: id });
      }
      return success;
    } catch (error) {
      console.error('Failed to remove object:', error);
      return false;
    }
  };

  const markFirstVisitComplete = () => {
    const success = storage.markFirstVisitComplete();
    if (success) {
      dispatch({ type: 'SET_FIRST_VISIT', payload: false });
    }
  };

  const markInstallPromptShown = () => {
    const success = storage.markInstallPromptShown();
    if (success) {
      dispatch({ type: 'SET_INSTALL_PROMPT', payload: false });
    }
  };

  const completeOnboarding = () => {
    const success = storage.markOnboardingComplete();
    if (success) {
      dispatch({ type: 'SET_ONBOARDING_COMPLETED', payload: true });
    }
  };

  const setAddObjectFlow = (flow: AddObjectFlow | null) => {
    uiDispatch({ type: 'SET_ADD_FLOW', payload: flow });
  };

  const refreshObjects = () => {
    dispatch({ type: 'REFRESH_OBJECTS' });
  };

  const clearAllData = () => {
    const success = storage.clearAllData();
    if (success) {
      dispatch({ type: 'LOAD_STATE', payload: initialState });
      uiDispatch({ type: 'SET_ADD_FLOW', payload: null });
    }
  };

  const exportData = () => {
    return storage.exportData();
  };

  const importData = (data: Partial<CompanionState>) => {
    const success = storage.importData(data);
    if (success) {
      const newState = storage.getCompanionState();
      dispatch({ type: 'LOAD_STATE', payload: newState });
    }
    return success;
  };

  // Context value
  const contextValue: CompanionContextType = {
    // State
    ...state,
    addObjectFlow: uiState.addObjectFlow,
    platformCapabilities,
    
    // Actions
    registerObject,
    updateObject,
    updateObjectConfiguration,
    removeObject,
    markFirstVisitComplete,
    markInstallPromptShown,
    completeOnboarding,
    setAddObjectFlow,
    refreshObjects,
    clearAllData,
    exportData,
    importData,
  };

  return (
    <CompanionContext.Provider value={contextValue}>
      {children}
    </CompanionContext.Provider>
  );
}

// Hook to use the context
export function useCompanion() {
  const context = useContext(CompanionContext);
  if (!context) {
    throw new Error('useCompanion must be used within a CompanionProvider');
  }
  return context;
}

// Convenience hooks for common patterns
export function useRegisteredObjects() {
  const { registeredObjects } = useCompanion();
  return registeredObjects;
}

export function useRegisteredObject(id: string) {
  const { registeredObjects } = useCompanion();
  return registeredObjects.find(obj => obj.id === id) || null;
}

export function useRegisteredObjectByTagId(tagId: string) {
  const { registeredObjects } = useCompanion();
  return registeredObjects.find(obj => obj.tagId === tagId) || null;
}

export function useObjectRegistration() {
  const { registerObject, addObjectFlow, setAddObjectFlow } = useCompanion();
  
  const startRegistration = () => {
    setAddObjectFlow({ step: 'select' });
  };
  
  const selectObject = (objectId: string) => {
    setAddObjectFlow({ 
      step: 'setup', 
      selectedObjectId: objectId 
    });
  };
  
  const completeSetup = async (
    setupData?: Partial<ObjectConfiguration>,
    tagId?: string,
    label?: string,
    nickname?: string
  ): Promise<RegisteredObject | null> => {
    if (!addObjectFlow?.selectedObjectId) return null;

    let finalSetupData = setupData;

    // If tagId is provided, check if configuration already exists on server
    if (tagId && addObjectFlow.selectedObjectId) {
      const { fetchObjectConfiguration } = await import('../utils/api');
      const result = await fetchObjectConfiguration(addObjectFlow.selectedObjectId, tagId);

      if (result.success && result.configuration) {
        // Use existing server configuration
        // Determine the configuration key based on object type
        const configKey = getConfigKeyForObject(addObjectFlow.selectedObjectId);
        if (configKey) {
          finalSetupData = {
            ...setupData,
            [configKey]: result.configuration,
          };
        }
      }
    }

    const newObject = await registerObject({
      objectId: addObjectFlow.selectedObjectId,
      setupData: finalSetupData,
      tagId,
      label,
      nickname,
    });

    if (newObject) {
      setAddObjectFlow(null);
      return newObject;
    }

    return null;
  };
  
  const cancelRegistration = () => {
    setAddObjectFlow(null);
  };
  
  return {
    flow: addObjectFlow,
    startRegistration,
    selectObject,
    completeSetup,
    cancelRegistration,
  };
}

// Platform capability hooks
export function usePlatformCapabilities() {
  const { platformCapabilities } = useCompanion();
  return platformCapabilities;
}

export function useInstallPrompt() {
  const { showInstallPrompt, markInstallPromptShown } = useCompanion();
  const [deferredPrompt, setDeferredPrompt] = React.useState<any>(null);
  
  React.useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);
  
  const triggerInstall = async () => {
    if (!deferredPrompt) return false;
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    markInstallPromptShown();
    
    return outcome === 'accepted';
  };
  
  return {
    canPrompt: showInstallPrompt && deferredPrompt,
    triggerInstall,
    dismissPrompt: markInstallPromptShown,
  };
}